import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  deleteBookRoyalty,
  getBook,
  getBookAuthors,
  getBookPrices,
  getBookRoyalties,
  updateBookPrice,
  updateBookRoyalty,
} from '../services/book_service'
import './BookDetail.css'

const currency = new Intl.NumberFormat('en-GH', {
  style: 'currency',
  currency: 'GHS',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 2,
})

const toNumber = (value) => Number(value ?? 0)

function BookDetail({
  basePath = '/admin/books/publish-books',
  breadcrumb = 'Publish Books',
  showAuthors = true,
}) {
  const { bookId } = useParams()
  const { user: authUser } = useAuth()
  const [book, setBook] = useState(null)
  const [royalties, setRoyalties] = useState([])
  const [prices, setPrices] = useState([])
  const [authorOptions, setAuthorOptions] = useState([])
  const [royaltyDraft, setRoyaltyDraft] = useState(null)
  const [priceDraft, setPriceDraft] = useState(null)
  const [royaltyToDelete, setRoyaltyToDelete] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const loadBook = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const [loadedBook, loadedRoyalties, loadedPrices] = await Promise.all([
        getBook(bookId),
        showAuthors ? getBookRoyalties(bookId) : Promise.resolve([]),
        getBookPrices(bookId),
      ])
      setBook(loadedBook)
      setRoyalties(loadedRoyalties)
      setPrices(loadedPrices)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }, [bookId, showAuthors])

  useEffect(() => {
    loadBook()
  }, [loadBook])

  useEffect(() => {
    if (!showAuthors) return
    getBookAuthors()
      .then(setAuthorOptions)
      .catch((requestError) => setError(requestError.message))
  }, [showAuthors])

  const userid = authUser?.userid ?? authUser?.user_id ?? authUser?.id

  const saveRoyalty = async () => {
    setIsSaving(true)
    setError('')
    try {
      await updateBookRoyalty(royaltyDraft.book_authorid, {
        authorid: royaltyDraft.authorid,
        royalty: royaltyDraft.royalty,
        userid,
      })
      setRoyaltyDraft(null)
      await loadBook()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  const removeRoyalty = async () => {
    setIsSaving(true)
    setError('')
    try {
      await deleteBookRoyalty(royaltyToDelete.book_authorid)
      setRoyaltyToDelete(null)
      await loadBook()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  const savePrice = async () => {
    setIsSaving(true)
    setError('')
    try {
      await updateBookPrice(
        priceDraft.customertypeunitpriceid,
        priceDraft.ctype_unitprice,
      )
      setPriceDraft(null)
      await loadBook()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className='book-detail'>
      <nav className='book-detail__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <Link to={basePath}>{breadcrumb}</Link>
        <span aria-hidden='true'>/</span>
        <strong>{book?.book_title || 'Book'}</strong>
      </nav>
      <header className='book-detail__header'>
        <div>
          <span className='app-content__eyebrow'>Book profile</span>
          <h1>{book?.book_title || 'Book details'}</h1>
          <p>Pricing, royalties, and stock movement for this title.</p>
        </div>
      </header>
      {error && (
        <div className='book-detail__error' role='alert'>
          {error}
          <button type='button' onClick={loadBook}>
            Retry
          </button>
        </div>
      )}
      {isLoading ? (
        <p className='book-detail__loading'>Loading book details...</p>
      ) : !book ? (
        <p className='book-detail__loading'>Book not found.</p>
      ) : (
        <div className='book-detail__grid'>
          <section className='book-card' aria-labelledby='book-info-title'>
            <h2 id='book-info-title'>Book information</h2>
            <dl className='book-info'>
              <div>
                <dt>Book ISBN</dt>
                <dd>{book.isbn || '-'}</dd>
              </div>
              <div>
                <dt>Book title</dt>
                <dd>{book.book_title || '-'}</dd>
              </div>
              <div>
                <dt>Selling price</dt>
                <dd>{currency.format(toNumber(book.unitprice))}</dd>
              </div>
              {showAuthors && (
                <div>
                  <dt>Authors</dt>
                  <dd>
                    {royalties.length
                      ? royalties.map((entry) => entry.author_name).join(', ')
                      : '-'}
                  </dd>
                </div>
              )}
            </dl>
          </section>

          {showAuthors && (
            <section className='book-card' aria-labelledby='book-royalty-title'>
              <h2 id='book-royalty-title'>Authors and royalties</h2>
              <table className='book-detail-table'>
                <thead>
                  <tr>
                    <th>Author</th>
                    <th>Royalty (%)</th>
                    <th>
                      <span className='sr-only'>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {royalties.length ? (
                    royalties.map((entry) => {
                      const isEditing =
                        royaltyDraft?.book_authorid === entry.book_authorid
                      return (
                        <tr key={entry.book_authorid}>
                          <td>
                            {isEditing ? (
                              <select
                                value={royaltyDraft.authorid}
                                onChange={(event) =>
                                  setRoyaltyDraft((draft) => ({
                                    ...draft,
                                    authorid: event.target.value,
                                  }))
                                }
                              >
                                {authorOptions.map((author) => (
                                  <option
                                    key={author.authorid}
                                    value={String(author.authorid)}
                                  >
                                    {author.author_name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              entry.author_name
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type='number'
                                min='0'
                                max='100'
                                step='0.01'
                                value={royaltyDraft.royalty}
                                onChange={(event) =>
                                  setRoyaltyDraft((draft) => ({
                                    ...draft,
                                    royalty: event.target.value,
                                  }))
                                }
                              />
                            ) : (
                              `${toNumber(entry.royalty)}%`
                            )}
                          </td>
                          <td>
                            <div className='book-detail-actions'>
                              {isEditing ? (
                                <>
                                  <button
                                    type='button'
                                    onClick={saveRoyalty}
                                    disabled={isSaving}
                                  >
                                    {isSaving ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => setRoyaltyDraft(null)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type='button'
                                    onClick={() =>
                                      setRoyaltyDraft({
                                        book_authorid: entry.book_authorid,
                                        authorid: String(entry.authorid),
                                        royalty: entry.royalty ?? '',
                                      })
                                    }
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className='book-detail-actions__delete'
                                    type='button'
                                    onClick={() => setRoyaltyToDelete(entry)}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td className='book-detail-table__empty' colSpan='3'>
                        No authors linked to this book.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          <section className='book-card' aria-labelledby='book-price-title'>
            <h2 id='book-price-title'>Customer type prices</h2>
            <table className='book-detail-table'>
              <thead>
                <tr>
                  <th>Customer type</th>
                  <th>Unit price</th>
                  <th>
                    <span className='sr-only'>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {prices.length ? (
                  prices.map((price) => {
                    const isEditing =
                      priceDraft?.customertypeunitpriceid ===
                      price.customertypeunitpriceid
                    return (
                      <tr key={price.customertypeunitpriceid}>
                        <td>{price.customer_type || '-'}</td>
                        <td>
                          {isEditing ? (
                            <input
                              type='number'
                              min='0'
                              step='0.01'
                              value={priceDraft.ctype_unitprice}
                              onChange={(event) =>
                                setPriceDraft((draft) => ({
                                  ...draft,
                                  ctype_unitprice: event.target.value,
                                }))
                              }
                            />
                          ) : (
                            currency.format(toNumber(price.ctype_unitprice))
                          )}
                        </td>
                        <td>
                          <div className='book-detail-actions'>
                            {isEditing ? (
                              <>
                                <button
                                  type='button'
                                  onClick={savePrice}
                                  disabled={isSaving}
                                >
                                  {isSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  type='button'
                                  onClick={() => setPriceDraft(null)}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                type='button'
                                onClick={() =>
                                  setPriceDraft({
                                    customertypeunitpriceid:
                                      price.customertypeunitpriceid,
                                    ctype_unitprice:
                                      price.ctype_unitprice ?? '',
                                  })
                                }
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td className='book-detail-table__empty' colSpan='3'>
                      No customer type prices set.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <section className='book-card' aria-labelledby='book-stock-title'>
            <h2 id='book-stock-title'>Stock details</h2>
            <div className='book-stock-grid'>
              <div className='book-stock-tile book-stock-tile--teal'>
                <small>In stock</small>
                <strong>{toNumber(book.instock)}</strong>
              </div>
              <div className='book-stock-tile book-stock-tile--orange'>
                <small>Bad books</small>
                <strong>{toNumber(book.bad)}</strong>
              </div>
              <div className='book-stock-tile book-stock-tile--blue'>
                <small>Unreturned samples</small>
                <strong>{toNumber(book.samples_not_returned)}</strong>
              </div>
              <div className='book-stock-tile book-stock-tile--red'>
                <small>Missing books</small>
                <strong>{toNumber(book.missing)}</strong>
              </div>
            </div>
          </section>
        </div>
      )}
      {royaltyToDelete && (
        <div
          className='book-detail-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && setRoyaltyToDelete(null)
          }
        >
          <section
            className='book-detail-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='royalty-delete-title'
          >
            <h2 id='royalty-delete-title'>Remove author?</h2>
            <p>
              This removes <strong>{royaltyToDelete.author_name}</strong> and
              their royalty from this book.
            </p>
            <div className='book-detail-modal__actions'>
              <button type='button' onClick={() => setRoyaltyToDelete(null)}>
                Cancel
              </button>
              <button
                className='book-detail-modal__danger'
                type='button'
                onClick={removeRoyalty}
                disabled={isSaving}
              >
                {isSaving ? 'Removing...' : 'Remove author'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default BookDetail
